import { Trans, useTranslation } from 'react-i18next';
import FlowingMenu, { type FlowingMenuEntry, type FlowingMenuItem } from '../../effects/FlowingMenu/FlowingMenu';
import Marker from '../../ui/Marker/Marker';
import Section from '../../ui/Section/Section';
import SectionHeader from '../../ui/SectionHeader/SectionHeader';
import { services, type ServiceShowcase } from '../../../data/services';
import { useLanguage } from '../../../i18n/useLanguage';
import type { TitledDetail } from '../../../locales/types';

function useShowcaseEntry(): (showcase: ServiceShowcase) => FlowingMenuEntry {
    const { t } = useTranslation();
    return (showcase) => {
        switch (showcase.kind) {
            case 'app':
                return { label: showcase.name, media: { kind: 'icon', src: showcase.iconSrc } };
            case 'website':
                return { label: showcase.host, media: { kind: 'window', src: showcase.screenshotSrc } };
            case 'technology':
                return { label: showcase.logo.name, media: { kind: 'glyph', path: showcase.logo.path } };
            case 'aiTask':
                return { label: t(`home.services.tasks.${showcase.task}`), media: { kind: 'icon', src: showcase.iconSrc } };
        }
    };
}

export default function ServicesSection() {
    const { t } = useTranslation();
    const { localizedPath } = useLanguage();
    const toEntry = useShowcaseEntry();

    const items: FlowingMenuItem[] = services.map((service) => {
        const copy = t(`home.services.items.${service.id}`, { returnObjects: true }) as unknown as TitledDetail;
        return { href: localizedPath(service.path), title: copy.title, detail: copy.detail, entries: service.showcase.map(toEntry) };
    });

    return (
        <Section theme="light" shape="sheet" id="services" labelledBy="services-title">
            <div className="page-inner">
                <SectionHeader id="services-title" title={t('home.services.title')} lead={<Trans i18nKey="home.services.lead" components={{ mark: <Marker /> }} />} />
                <FlowingMenu items={items} />
            </div>
        </Section>
    );
}
